import { HYPERLIQUID_INFO_URL } from '@/constants/hyperliquid'
import { CHAIN_CONFIG } from '@/lib/api/relay'
import type { MarketBalance, MarketWalletProvider, TokenBalance } from '../types'

const ARB_USDC_DECIMALS = 6

/**
 * Hyperliquid wallet provider for the internal Bridge / cash-routing system.
 *
 * Two important subtleties:
 *
 *   1. `resolveAddress` returns the user's primary EOA on Arbitrum. The
 *      internal Bridge transfers USDC TO this address; the auto-deposit
 *      watcher (`useHyperliquidAutoDeposit`) sees the Arbitrum balance bump
 *      and runs Bridge2.deposit on the user's behalf. From the user's POV
 *      the Bridge modal shows "destination: Hyperliquid", and ~4min later
 *      the HL clearinghouse balance reflects the deposit.
 *
 *   2. `readBalance` returns the HyperCore clearinghouse `withdrawable`,
 *      NOT the user's Arbitrum USDC balance. The "balance" shown in the
 *      Bridge modal is what the user actually has on HL — which is what
 *      they can spend on trading. Arbitrum USDC is in-flight buffer, not
 *      a HL balance.
 *
 * Chain config points at Arbitrum One — that's the route Relay needs to
 * settle the cross-chain leg before the watcher takes over.
 */
/**
 * "HL Cash" displayed in the portfolio header AND the source-balance
 * read by the Bridge modal when From=Hyperliquid. Returns the user's
 * TOTAL withdrawable holdings across perp + spot USDC + spot USDH.
 * USDH is included because the withdrawal pipeline can convert it
 * (sell USDH→USDC, transfer spot→perp, withdraw3).
 */
async function readHyperliquidTotalBalance(primaryAddress: string): Promise<number> {
	try {
		const [perpRes, spotRes] = await Promise.all([
			fetch(`${HYPERLIQUID_INFO_URL}/info`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ type: 'clearinghouseState', user: primaryAddress }),
			}),
			fetch(`${HYPERLIQUID_INFO_URL}/info`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ type: 'spotClearinghouseState', user: primaryAddress }),
			}),
		])

		let total = 0
		if (perpRes.ok) {
			const perp = (await perpRes.json()) as { withdrawable?: string } | null
			const n = Number.parseFloat(perp?.withdrawable ?? '0')
			if (Number.isFinite(n)) total += n
		}
		if (spotRes.ok) {
			const spot = (await spotRes.json()) as {
				balances?: Array<{ coin: string; total: string }>
			} | null
			for (const b of spot?.balances ?? []) {
				if (b.coin === 'USDC' || b.coin === 'USDH') {
					const n = Number.parseFloat(b.total)
					if (Number.isFinite(n)) total += n
				}
			}
		}
		return total
	} catch {
		return 0
	}
}

function buildCashTokenBalance(amount: number): TokenBalance {
	const raw = BigInt(Math.round(amount * 10 ** ARB_USDC_DECIMALS))
	return {
		symbol: 'USDC',
		raw,
		decimals: ARB_USDC_DECIMALS,
		amount,
		formatted: amount.toFixed(2),
		tokenAddress: CHAIN_CONFIG.arbitrum.usdc,
	}
}

export const hyperliquidWalletProvider: MarketWalletProvider = {
	id: 'hyperliquid',
	label: 'Hyperliquid',
	direction: 'toHyperliquid',
	decimals: ARB_USDC_DECIMALS,
	chain: { chainId: CHAIN_CONFIG.arbitrum.id, currency: CHAIN_CONFIG.arbitrum.usdc },
	cashToken: {
		symbol: 'USDC',
		address: CHAIN_CONFIG.arbitrum.usdc,
		decimals: ARB_USDC_DECIMALS,
	},
	resolveAddress: runtime =>
		runtime.addressOverrides?.hyperliquid ?? runtime.eoaAddress ?? undefined,
	balanceQueryKey: address => ['market-balance', 'hyperliquid', address] as const,
	async readBalance(runtime): Promise<MarketBalance | null> {
		const address = this.resolveAddress(runtime)
		if (!address) return null
		const amount = await readHyperliquidTotalBalance(address)
		return {
			platform: 'hyperliquid',
			address,
			cash: buildCashTokenBalance(amount),
		}
	},
}
